import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';

// The floating tools sit above every dialog and drawer (a MUI modal is z-index 1300) so they can be used while a form
// is open, and below the snackbar (1400) and tooltips (1500).
export const Z_BASE = 1350;
// A tool's PAGE on a phone covers the whole screen, so nothing of the app may be drawn over it: the install / notification prompts are
// bottom sheets at 1600 and sat on top of the pinned Apply button and the keypad's last row. (Desktop windows stay at Z_BASE.)
export const Z_PAGE = 1700;

// Design tokens in the app's dark, opacity-based language (see theme/theme.js): white at low opacity on near-black,
// black at low opacity on white; the primary action is solid white on dark and solid black on light.
export function useToolTokens() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return useMemo(() => ({
    isDark,
    rtl: theme.direction === 'rtl',
    BG: isDark ? '#111111' : '#ffffff',
    HEAD: isDark ? '#181818' : '#f5f5f5',
    BD: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.12)',
    BD2: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.20)',
    TEXT: isDark ? 'rgba(255,255,255,0.87)' : 'rgba(0,0,0,0.87)',
    TEXT2: isDark ? 'rgba(255,255,255,0.50)' : 'rgba(0,0,0,0.55)',
    TEXT3: isDark ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.32)',
    CTRL: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    CTRL_HOVER: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.09)',
    ACCENT: isDark ? '#ffffff' : '#000000',
    ON_ACCENT: isDark ? '#000000' : '#ffffff',
    RANGE: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)',
    SHADOW: isDark ? '0 18px 50px rgba(0,0,0,0.60)' : '0 18px 50px rgba(0,0,0,0.22)',
  }), [isDark, theme.direction]);
}

// A MUI Dialog / Drawer traps focus: the moment focus lands anywhere outside it, it takes it straight back. A tool is
// rendered outside every modal, so while one is open a tool must not take focus at all - which also means a press on a
// tool's button leaves the cursor where it was in the person's form, the way a calculator should.
// A closed modal does not count: the phone menu's SwipeableDrawer stays mounted (hidden) at every screen size, and a hidden drawer
// traps nothing - counting it made this answer "yes" all the time, so a tool never focused itself and its own keyboard never worked.
export const modalIsOpen = () => {
  if (typeof document === 'undefined') return false;
  return [...document.querySelectorAll('.MuiModal-root')].some((el) => {
    if (el.classList.contains('MuiModal-hidden')) return false;
    const style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden';
  });
};
export const keepFocus = (e) => { if (modalIsOpen()) e.preventDefault(); };

/** Focus a tool's own root when it opens, so the keyboard works at once - unless a form is open under it (see modalIsOpen). */
export function useFocusOnOpen(ref) {
  useEffect(() => {
    if (!modalIsOpen() && ref.current) ref.current.focus({ preventScroll: true });
  }, [ref]);
}

/** The size of an element, kept current with a ResizeObserver: { w, h } (0 × 0 until it is first measured). */
export function useElementSize(ref) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setSize((prev) => (Math.round(prev.w) === Math.round(r.width) && Math.round(prev.h) === Math.round(r.height) ? prev : { w: r.width, h: r.height }));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}
